import { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RefreshCw, TrendingUp, Phone, CheckCircle, Users, Search, ChevronLeft, ChevronRight, DollarSign, Link, X, Check, Eye, MessageSquare, FileText, LogOut, Shield, Settings } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Toaster } from "@/components/ui/toaster";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Login from "./Login";
import AdminPanel from "./AdminPanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  
  // Admin view state
  const [allUsers, setAllUsers] = useState([]);
  const [viewingUserId, setViewingUserId] = useState(null);
  const [viewingUser, setViewingUser] = useState(null);
  
  // Existing state
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [currentSheetsUrl, setCurrentSheetsUrl] = useState("");
  const [newSheetsUrl, setNewSheetsUrl] = useState("");
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [dealSize, setDealSize] = useState(5000); // Default deal size for insurance
  const [callAttemptsFilter, setCallAttemptsFilter] = useState("all"); // Filter for call attempts
  const [cardFilter, setCardFilter] = useState(null); // Filter based on clicked card
  const leadsPerPage = 20;
  const { toast } = useToast();

  // Check authentication status
  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      
      setIsAuthenticated(true);
      setCurrentUser(response.data.user);
      setCurrentSheetsUrl(response.data.user.sheets_url || "");
      return response.data.user;
    } catch (error) {
      console.log("Not authenticated");
      setIsAuthenticated(false);
      setCurrentUser(null);
      return null;
    } finally {
      setAuthLoading(false);
    }
  };

  // Handle login success
  const handleLoginSuccess = (user) => {
    console.log('[handleLoginSuccess] User logged in:', user.email, 'Has sheets_url:', !!user.sheets_url);
    setIsAuthenticated(true);
    setCurrentUser(user);
    setCurrentSheetsUrl(user.sheets_url || "");
    
    // Auto-fetch analytics if user has sheets_url
    if (user.sheets_url) {
      console.log('[handleLoginSuccess] Triggering fetchAnalytics...');
      fetchAnalytics(false, user.sheets_url);
    } else {
      console.log('[handleLoginSuccess] No sheets_url found for user');
    }
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        withCredentials: true
      });
      
      setIsAuthenticated(false);
      setCurrentUser(null);
      setMetrics(null);
      setShowAdminPanel(false);
      setViewingUserId(null);
      setViewingUser(null);
      
      toast({
        title: "Success",
        description: "Logged out successfully",
      });
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  // Fetch all users for admin dropdown
  const fetchAllUsers = async () => {
    if (currentUser?.role !== "admin") return;
    
    try {
      const response = await axios.get(`${API}/admin/users`, {
        withCredentials: true
      });
      if (response.data && Array.isArray(response.data.users)) {
        setAllUsers(response.data.users);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      toast({
        title: "Error",
        description: "Failed to load users list",
        variant: "destructive",
      });
    }
  };

  // Switch to viewing another user's dashboard (admin only)
  const switchToUserView = async (userId) => {
    if (currentUser?.role !== "admin") return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/users/${userId}/dashboard`, {
        withCredentials: true
      });
      
      const user = allUsers.find(u => u.id === userId);
      setViewingUserId(userId);
      setViewingUser(user);
      setMetrics(response.data);
      setLastUpdated(new Date().toISOString());
      
      toast({
        title: "Success",
        description: `Viewing ${user?.name}'s dashboard`,
      });
    } catch (error) {
      console.error("Error switching user view:", error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to load user dashboard",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Switch back to own dashboard
  const switchToOwnView = () => {
    setViewingUserId(null);
    setViewingUser(null);
    if (currentUser?.sheets_url) {
      fetchAnalytics(false, currentUser.sheets_url);
    } else {
      setMetrics(null);
    }
  };

  // Fetch analytics data
  const fetchAnalytics = async (useCache = false, sheetsUrl = null) => {
    setLoading(true);
    const urlToUse = sheetsUrl || currentSheetsUrl || currentUser?.sheets_url;
    
    console.log('[fetchAnalytics] Starting fetch. URL:', urlToUse, 'UseCache:', useCache);
    
    try {
      let response;
      if (useCache) {
        response = await axios.get(`${API}/analytics/cached`, {
          withCredentials: true
        });
      } else {
        response = await axios.post(`${API}/analytics/fetch`, {
          sheets_url: urlToUse
        }, {
          withCredentials: true
        });
      }
      
      console.log('[fetchAnalytics] Success! Data received:', {
        call_volume: response.data.call_volume,
        leads_count: response.data.leads_data?.length
      });
      
      setMetrics(response.data);
      setLastUpdated(new Date(response.data.last_updated));
      
      if (!useCache) {
        toast({
          title: "Data Updated",
          description: `Analytics data refreshed from ${sheetsUrl ? 'new' : 'current'} Google Sheets.`,
        });
      }
    } catch (error) {
      console.error('[fetchAnalytics] Error:', error);
      console.error('[fetchAnalytics] Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        detail: error.response?.data?.detail,
        url: urlToUse
      });
      
      toast({
        title: "Error Loading Dashboard",
        description: error.response?.data?.detail || "Failed to fetch analytics. Please try clicking the Refresh button or contact support.",
        variant: "destructive",
      });
      
      // Set metrics to null to show the "No data" state with Load Data button
      setMetrics(null);
    } finally {
      setLoading(false);
      console.log('[fetchAnalytics] Fetch complete');
    }
  };

  // Check authentication on mount
  useEffect(() => {
    checkAuth().then((user) => {
      if (user) {
        if (user.role === "admin") {
          // Fetch all users for admin dropdown
          fetchAllUsers();
        }
        if (user.sheets_url) {
          // Auto-load data if user is authenticated and has sheets_url
          fetchAnalytics(false, user.sheets_url);
        }
      }
    });
  }, []);

  // Auto-refresh every 5 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      fetchAnalytics(false);
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, []);

  // Format date only (no time)
  const formatDate = (utcDateString) => {
    if (!utcDateString) return '';
    
    try {
      // Parse the UTC date string and show date only
      const utcDate = new Date(utcDateString);
      
      return utcDate.toLocaleDateString('en-US', {
        timeZone: 'America/New_York',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Date unavailable';
    }
  };

  // Smart pagination - show max 15 pages with navigation
  const getVisiblePages = (totalPages) => {
    const maxVisiblePages = 15;
    if (totalPages <= maxVisiblePages) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const halfVisible = Math.floor(maxVisiblePages / 2);
    let start = Math.max(1, currentPage - halfVisible);
    let end = Math.min(totalPages, start + maxVisiblePages - 1);
    
    // Adjust start if we're near the end
    if (end - start + 1 < maxVisiblePages) {
      start = Math.max(1, end - maxVisiblePages + 1);
    }
    
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  // Get status color based on status type
  const getStatusColor = (status) => {
    const statusColors = {
      'Meeting Booked': 'bg-green-100 text-green-800 border-green-200',
      'Callback': 'bg-blue-100 text-blue-800 border-blue-200',
      'Voicemail': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'Hang Up': 'bg-red-100 text-red-800 border-red-200',
      'Wrong Number': 'bg-gray-100 text-gray-800 border-gray-200',
      'Not Interested': 'bg-red-100 text-red-800 border-red-200',
      'Not in Service': 'bg-gray-100 text-gray-800 border-gray-200',
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  // Calculate success rate
  const getSuccessRate = () => {
    if (!metrics || !metrics.status_counts) return 0;
    const successful = (metrics.status_counts['Meeting Booked'] || 0) + 
                      (metrics.status_counts['Booked Meeting'] || 0) + 
                      (metrics.status_counts['Callback'] || 0);
    return metrics.call_volume > 0 ? ((successful / metrics.call_volume) * 100).toFixed(1) : 0;
  };

  // Check if any leads have business names
  const hasBusinessNames = metrics?.leads_data?.some(lead => lead.business_name && lead.business_name.trim() !== '') || false;

  // Calculate uncalled leads count
  const uncalledLeadsCount = metrics?.leads_data?.filter(lead => lead.call_attempts === 0).length || 0;

  // Calculate completed leads based on:
  // 1. Leads with 4+ attempts
  // 2. Not Interested
  // 3. Booked meetings
  // 4. Not in service, number doesn't exist, wrong number
  // 5. Callback, No Answer
  // 6. Interested
  // 7. Lead score 7 or higher
  // 8. Leads without phone numbers
  // 9. Hang Up status
  const completedLeadsCount = metrics?.leads_data?.filter(lead => {
    const finalStatuses = [
      'not interested',
      'interested',
      'booked meeting',
      'meeting booked',
      'callback',
      'called back',
      'no answer',
      'not in service',
      'not in-service',
      'number does not exist',
      'wrong number',
      'hang up'
    ];
    const hasFinalStatus = finalStatuses.includes(lead.lead_status?.toLowerCase() || '');
    const hasHighScore = lead.lead_score >= 7;
    const noPhoneNumber = !lead.phone_number || lead.phone_number.trim() === '';
    
    return lead.call_attempts >= 4 || hasFinalStatus || hasHighScore || noPhoneNumber;
  }).length || 0;

  // Calculate remaining leads (not completed)
  const remainingLeadsCount = (metrics?.call_volume || 0) - completedLeadsCount;

  // Filter leads based on search term, call attempts filter, and card filter
  const filteredLeads = metrics?.leads_data?.filter(lead => {
    // Search term filter
    const matchesSearch = 
      lead.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.phone_number?.includes(searchTerm) ||
      lead.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.lead_status?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (lead.business_name && lead.business_name.toLowerCase().includes(searchTerm.toLowerCase()));
    
    // Call attempts filter
    let matchesCallAttempts = true;
    if (callAttemptsFilter === "0") {
      matchesCallAttempts = lead.call_attempts === 0;
    } else if (callAttemptsFilter === "1") {
      matchesCallAttempts = lead.call_attempts === 1;
    } else if (callAttemptsFilter === "2") {
      matchesCallAttempts = lead.call_attempts === 2;
    } else if (callAttemptsFilter === "3+") {
      matchesCallAttempts = lead.call_attempts >= 3;
    }
    // "all" means no filter
    
    // Card filter (when user clicks on a metric card)
    let matchesCardFilter = true;
    if (cardFilter) {
      if (cardFilter.type === 'uncalled') {
        matchesCardFilter = lead.call_attempts === 0;
      } else if (cardFilter.type === 'completed') {
        // Use same logic as completedLeadsCount
        const finalStatuses = [
          'not interested', 'interested', 'booked meeting', 'meeting booked',
          'callback', 'called back', 'no answer', 'not in service', 'not in-service',
          'number does not exist', 'wrong number', 'hang up'
        ];
        const hasFinalStatus = finalStatuses.includes(lead.lead_status?.toLowerCase() || '');
        const hasHighScore = lead.lead_score >= 7;
        const noPhoneNumber = !lead.phone_number || lead.phone_number.trim() === '';
        matchesCardFilter = lead.call_attempts >= 4 || hasFinalStatus || hasHighScore || noPhoneNumber;
      } else if (cardFilter.type === 'remaining') {
        // Opposite of completed
        const finalStatuses = [
          'not interested', 'interested', 'booked meeting', 'meeting booked',
          'callback', 'called back', 'no answer', 'not in service', 'not in-service',
          'number does not exist', 'wrong number', 'hang up'
        ];
        const hasFinalStatus = finalStatuses.includes(lead.lead_status?.toLowerCase() || '');
        const hasHighScore = lead.lead_score >= 7;
        const noPhoneNumber = !lead.phone_number || lead.phone_number.trim() === '';
        const isCompleted = lead.call_attempts >= 4 || hasFinalStatus || hasHighScore || noPhoneNumber;
        matchesCardFilter = !isCompleted;
      } else if (cardFilter.type === 'status') {
        matchesCardFilter = lead.lead_status?.toLowerCase() === cardFilter.value?.toLowerCase();
      }
    }
    
    return matchesSearch && matchesCallAttempts && matchesCardFilter;
  }) || [];

  // Pagination logic
  const totalPages = Math.ceil(filteredLeads.length / leadsPerPage);
  const currentLeads = filteredLeads.slice(
    (currentPage - 1) * leadsPerPage,
    currentPage * leadsPerPage
  );
  const visiblePages = getVisiblePages(totalPages);

  // Handle card click to filter leads
  const handleCardClick = (filterType, filterValue = null) => {
    if (cardFilter?.type === filterType && cardFilter?.value === filterValue) {
      // If clicking the same card, clear the filter
      setCardFilter(null);
    } else {
      setCardFilter({ type: filterType, value: filterValue });
    }
    setCurrentPage(1); // Reset to first page when filter changes
  };

  // Calculate ROI metrics (Insurance: based on interested leads only)
  const calculateROI = () => {
    if (!metrics || !metrics.leads_data) return { totalCommission: 0, potentialRevenue: 0, avgLoanAmount: dealSize, interestedLeads: 0 };
    
    // Count interested leads only (positive statuses)
    const interestedStatuses = ['interested', 'callback', 'called back', 'booked meeting', 'meeting booked', 'follow up'];
    const interestedLeads = metrics.leads_data.filter(lead => 
      interestedStatuses.includes(lead.lead_status?.toLowerCase() || '')
    ).length;
    
    // Insurance commission (smaller deals, higher percentage)
    const avgCommissionRate = 0.15; // 15% commission for insurance
    
    // Calculate based on interested leads only
    const potentialRevenue = interestedLeads * dealSize;
    const totalCommission = Math.round(potentialRevenue * avgCommissionRate);
    
    return { 
      totalCommission, 
      potentialRevenue: Math.round(potentialRevenue), 
      avgLoanAmount: dealSize,
      interestedLeads
    };
  };

  // Prepare chart data
  const getChartData = () => {
    if (!metrics || !metrics.status_counts) return { statusData: [], scoreData: [] };
    
    const safeCallVolume = metrics.call_volume || 1; // Prevent division by zero
    
    const statusData = Object.entries(metrics.status_counts).map(([status, count]) => ({
      name: status,
      value: count,
      percentage: ((count / safeCallVolume) * 100).toFixed(1)
    }));
    
    const scoreDistribution = {};
    if (Array.isArray(metrics.leads_data)) {
      metrics.leads_data.forEach(lead => {
        if (lead && typeof lead.lead_score === 'number') {
          const scoreRange = lead.lead_score >= 7 ? 'High (7-10)' : 
                            lead.lead_score >= 4 ? 'Medium (4-6)' : 'Low (1-3)';
          scoreDistribution[scoreRange] = (scoreDistribution[scoreRange] || 0) + 1;
        }
      });
    }
    
    const scoreData = Object.entries(scoreDistribution).map(([range, count]) => ({
      name: range,
      value: count,
      percentage: ((count / safeCallVolume) * 100).toFixed(1)
    }));
    
    return { statusData, scoreData };
  };

  const { statusData, scoreData } = getChartData();
  const roiMetrics = calculateROI();

  // Chart colors
  const statusColors = ['#004aad', '#0066cc', '#3399ff', '#66b3ff', '#99ccff', '#cce6ff', '#ff6b6b', '#ff9999'];
  const scoreColors = ['#004aad', '#004aad', '#004aad']; // WitsType blue for all bars

  // Landing Page Component
  const LandingPage = () => {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="max-w-4xl mx-auto px-6 py-12">
          <div className="text-center mb-12">
            {/* Logo and Title */}
            <div className="flex items-center justify-center gap-4 mb-8">
              <img 
                src={process.env.REACT_APP_LOGO_URL || "https://customer-assets.emergentagent.com/job_call-insights-hub-1/artifacts/e2228xl3_WitsType%20AI%20Logo%20%28Lion%29.jpg"} 
                alt="WitsType AI Logo" 
                className="h-16 w-16 object-contain"
              />
              <div>
                <h1 className="text-4xl font-bold text-slate-900" style={{fontFamily: 'League Spartan, sans-serif'}}>
                  WitsType AI Analytics Dashboard
                </h1>
                <p className="text-xl text-slate-600 mt-2">Transform your AI Voice Agent data into actionable insights</p>
              </div>
            </div>

            {/* Value Proposition */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              <div className="text-center p-6">
                <div className="w-16 h-16 bg-[#004aad]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="w-8 h-8 text-[#004aad]" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Real-Time Analytics</h3>
                <p className="text-slate-600">Track call volume, success rates, and lead performance in real-time</p>
              </div>
              
              <div className="text-center p-6">
                <div className="w-16 h-16 bg-[#004aad]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <DollarSign className="w-8 h-8 text-[#004aad]" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">ROI Calculator</h3>
                <p className="text-slate-600">Calculate potential commissions and revenue from your calling campaigns</p>
              </div>
              
              <div className="text-center p-6">
                <div className="w-16 h-16 bg-[#004aad]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-[#004aad]" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Lead Management</h3>
                <p className="text-slate-600">Detailed lead insights with call summaries, transcripts, and status tracking</p>
              </div>
            </div>

            {/* CTA Section */}
            <Card className="p-8 bg-white shadow-xl border-0 ring-1 ring-slate-200">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4" style={{fontFamily: 'League Spartan, sans-serif'}}>
                Get Started with Your Analytics
              </h2>
              <p className="text-slate-600 mb-6">
                Paste your Google Sheets shareable link below to instantly view your AI Voice Agent analytics
              </p>
              
              <div className="max-w-2xl mx-auto">
                <div className="flex gap-4 items-end">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Google Sheets URL
                    </label>
                    <Input
                      type="url"
                      value={landingPageUrl}
                      onChange={(e) => setLandingPageUrl(e.target.value)}
                      placeholder="https://docs.google.com/spreadsheets/d/YOUR_SHEET_ID/edit?usp=sharing"
                      className="text-sm"
                      data-testid="landing-page-url-input"
                    />
                  </div>
                  <Button 
                    onClick={handleLandingPageSubmit}
                    disabled={loading}
                    className="bg-[#004aad] hover:bg-[#003a8c] text-white px-8 py-2"
                    data-testid="view-analytics-button"
                  >
                    {loading ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      <>
                        <TrendingUp className="w-4 h-4 mr-2" />
                        View Analytics
                      </>
                    )}
                  </Button>
                </div>
                
                <p className="text-xs text-slate-500 mt-3 text-left">
                  <strong>How to get your shareable link:</strong>
                  <br />1. Open your Google Sheets document
                  <br />2. Click "Share" in the top-right corner
                  <br />3. Click "Copy link" and paste it above
                </p>
              </div>
            </Card>

            {/* Features List */}
            <div className="mt-12 text-left max-w-2xl mx-auto">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">What you'll get:</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-slate-700">Call volume and attempt tracking</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-slate-700">Success rate analysis</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-slate-700">Interactive ROI calculator</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-slate-700">Lead status breakdown</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-slate-700">Professional data visualization</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-slate-700">Real-time data updates</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Toaster />
      </div>
    );
  };

  // Handle lead row click to show details
  const handleLeadClick = (lead) => {
    setSelectedLead(lead);
    setShowLeadModal(true);
  };

  // Lead Details Modal Component
  const LeadDetailsModal = ({ lead, isOpen, onClose }) => {
    if (!lead) return null;

    // Safety check for call_summaries
    const callSummaries = Array.isArray(lead.call_summaries) ? lead.call_summaries : [];

    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold" style={{fontFamily: 'League Spartan, sans-serif'}}>
              Lead Details: {lead.first_name || 'Unknown'}
            </DialogTitle>
          </DialogHeader>
          
          <ScrollArea className="h-[60vh]">
            <div className="space-y-6">
              {/* Lead Summary */}
              <Card className="p-4">
                <h3 className="font-semibold mb-3">Contact Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-600">Name</p>
                    <p className="font-medium">{lead.first_name || 'N/A'}</p>
                  </div>
                  {lead.business_name && lead.business_name.trim() !== '' && (
                    <div>
                      <p className="text-sm text-slate-600">Company Name</p>
                      <p className="font-medium">{lead.business_name}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-slate-600">Phone</p>
                    <p className="font-medium">{lead.phone_number || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Email</p>
                    <p className="font-medium">{lead.email || 'N/A'}</p>
                  </div>
                  {lead.province && lead.province.trim() !== '' && (
                    <div>
                      <p className="text-sm text-slate-600">Province</p>
                      <p className="font-medium">{lead.province}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-slate-600">Status</p>
                    <Badge className={getStatusColor(lead.lead_status)}>
                      {lead.lead_status || 'Unknown'}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Lead Score</p>
                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold ${
                      lead.lead_score >= 7 ? 'bg-green-100 text-green-800' :
                      lead.lead_score >= 4 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {lead.lead_score}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Call Attempts</p>
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-800 text-xs font-bold">
                      {lead.call_attempts}
                    </span>
                  </div>
                </div>
                
                {(lead.property_address || lead.mortgage_balance) && (
                  <div className="mt-4 pt-4 border-t">
                    <h4 className="font-medium mb-2">Property Information</h4>
                    <div className="grid grid-cols-2 gap-4">
                      {lead.property_address && (
                        <div>
                          <p className="text-sm text-slate-600">Property Address</p>
                          <p className="font-medium">{lead.property_address}</p>
                        </div>
                      )}
                      {lead.mortgage_balance && (
                        <div>
                          <p className="text-sm text-slate-600">Mortgage Balance</p>
                          <p className="font-medium">
                            {!isNaN(parseInt(lead.mortgage_balance)) 
                              ? `$${parseInt(lead.mortgage_balance).toLocaleString()}`
                              : lead.mortgage_balance
                            }
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </Card>

              {/* Call History */}
              {callSummaries.length > 0 && (
                <Card className="p-4">
                  <h3 className="font-semibold mb-3">Call History ({callSummaries.length} calls)</h3>
                  
                  <Tabs defaultValue="summaries" className="w-full">
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="summaries" className="flex items-center gap-2">
                        <MessageSquare className="w-4 h-4" />
                        Call Summaries
                      </TabsTrigger>
                      <TabsTrigger value="transcripts" className="flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        Transcripts
                      </TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="summaries" className="space-y-3">
                      {callSummaries.map((call) => (
                        <div key={call.call_number} className="border rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline">Call #{call.call_number}</Badge>
                              <Badge className={call.direction === "inbound" ? "bg-green-100 text-green-800" : "bg-blue-100 text-blue-800"}>
                                {call.direction === "inbound" ? "↓ Inbound" : "↑ Outbound"}
                              </Badge>
                            </div>
                            {call.recording_url && (
                              <a
                                href={call.recording_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-blue-600 hover:text-blue-800 underline flex items-center gap-1"
                              >
                                <Phone className="w-3 h-3" />
                                Recording
                              </a>
                            )}
                          </div>
                          <p className="text-sm text-slate-700">
                            {call.summary || 'No summary available'}
                          </p>
                        </div>
                      ))}
                    </TabsContent>
                    
                    <TabsContent value="transcripts" className="space-y-3">
                      {callSummaries.map((call) => (
                        <div key={call.call_number} className="border rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline">Call #{call.call_number}</Badge>
                              <Badge className={call.direction === "inbound" ? "bg-green-100 text-green-800" : "bg-blue-100 text-blue-800"}>
                                {call.direction === "inbound" ? "↓ Inbound" : "↑ Outbound"}
                              </Badge>
                            </div>
                            {call.recording_url && (
                              <a
                                href={call.recording_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-blue-600 hover:text-blue-800 underline flex items-center gap-1"
                              >
                                <Phone className="w-3 h-3" />
                                Recording
                              </a>
                            )}
                          </div>
                          <div className="text-sm text-slate-700 whitespace-pre-wrap">
                            {call.transcript || 'No transcript available'}
                          </div>
                        </div>
                      ))}
                    </TabsContent>
                  </Tabs>
                </Card>
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    );
  };

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return (
      <>
        <Login onLoginSuccess={handleLoginSuccess} />
        <Toaster />
      </>
    );
  }

  // Show admin panel if requested
  if (showAdminPanel && currentUser?.role === "admin") {
    return (
      <>
        <div className="border-b bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <img 
                  src={process.env.REACT_APP_LOGO_URL || "https://customer-assets.emergentagent.com/job_call-insights-hub-1/artifacts/e2228xl3_WitsType%20AI%20Logo%20%28Lion%29.jpg"} 
                  alt="WitsType AI Logo" 
                  className="h-12 w-12 object-contain"
                />
                <h1 className="text-2xl font-bold text-slate-900">Admin Panel</h1>
              </div>
              <div className="flex items-center gap-3">
                <Button onClick={() => setShowAdminPanel(false)} variant="outline">
                  Back to Dashboard
                </Button>
                <Button onClick={handleLogout} variant="outline" className="text-red-600">
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </Button>
              </div>
            </div>
          </div>
        </div>
        <AdminPanel currentUser={currentUser} />
        <Toaster />
      </>
    );
  }

  // Main Dashboard
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <div className="border-b bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src={process.env.REACT_APP_LOGO_URL || "https://customer-assets.emergentagent.com/job_call-insights-hub-1/artifacts/e2228xl3_WitsType%20AI%20Logo%20%28Lion%29.jpg"} 
                alt="WitsType AI Logo" 
                className="h-12 w-12 object-contain"
              />
              <div>
                <h1 className="text-2xl font-bold text-slate-900" style={{fontFamily: 'League Spartan, sans-serif'}}>
                  {process.env.REACT_APP_DASHBOARD_TITLE || "WitsType AI Analytics Dashboard"}
                </h1>
                <p className="text-slate-600 mt-1">Live dashboard for outbound call performance</p>
                {metrics && currentUser?.sheets_url && (
                  <p className="text-xs text-slate-400 mt-1">
                    Data source: {currentUser.sheets_url.split('/')[5]?.substring(0, 20)}...
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              {lastUpdated && (
                <div className="text-sm text-slate-500">
                  Last updated: {formatDate(lastUpdated)}
                </div>
              )}
              
              {/* Admin User Selector Dropdown */}
              {currentUser?.role === "admin" && allUsers.length > 0 && (
                <div>
                  <label className="text-xs text-slate-600 block mb-1">Viewing Dashboard:</label>
                  <select
                    value={viewingUserId || currentUser.id}
                    onChange={(e) => {
                      if (e.target.value === currentUser.id) {
                        switchToOwnView();
                      } else {
                        switchToUserView(e.target.value);
                      }
                    }}
                    className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                  >
                    <option value={currentUser.id}>My Dashboard</option>
                    {allUsers.filter(u => u.id !== currentUser.id).map(user => (
                      <option key={user.id} value={user.id}>
                        {user.name} ({user.email})
                      </option>
                    ))}
                  </select>
                </div>
              )}
              
              {/* User Info & Actions */}
              <div className="flex items-center gap-2 px-3 py-2 bg-slate-100 rounded-lg">
                <div className="flex items-center gap-2">
                  {(viewingUser || currentUser)?.picture ? (
                    <img src={(viewingUser || currentUser).picture} alt={(viewingUser || currentUser).name} className="w-8 h-8 rounded-full" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold">
                      {(viewingUser || currentUser)?.name?.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-medium text-slate-900">
                      {viewingUser ? viewingUser.name : currentUser?.name}
                      {viewingUser && <span className="text-xs text-blue-600 ml-1">(viewing)</span>}
                    </p>
                    <p className="text-xs text-slate-500">{(viewingUser || currentUser)?.role}</p>
                  </div>
                </div>
              </div>

              {/* Google Sheets Button */}
              {(viewingUser?.sheets_url || currentUser?.sheets_url || currentSheetsUrl) && (
                <Button
                  onClick={() => {
                    const url = viewingUser?.sheets_url || currentUser?.sheets_url || currentSheetsUrl;
                    window.open(url, '_blank');
                  }}
                  variant="outline"
                  className="border-green-600 text-green-600 hover:bg-green-600 hover:text-white"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  View Sheet
                </Button>
              )}

              {/* Admin Panel Button */}
              {currentUser?.role === "admin" && (
                <Button onClick={() => setShowAdminPanel(true)} variant="outline">
                  <Shield className="w-4 h-4 mr-2" />
                  Admin
                </Button>
              )}
              
              {/* Refresh Button */}
              <Button 
                  onClick={() => {
                    if (viewingUserId) {
                      switchToUserView(viewingUserId);
                    } else {
                      fetchAnalytics(false);
                    }
                  }} 
                  disabled={loading}
                  variant="outline"
                  className="border-[#004aad] text-[#004aad] hover:bg-[#004aad] hover:text-white"
                  data-testid="refresh-button"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>

              {/* Logout Button */}
              <Button onClick={handleLogout} variant="outline" className="text-red-600">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Manual Google Sheets URL Input if none assigned */}
      {!currentUser?.sheets_url && (
        <div className="max-w-7xl mx-auto px-6 py-8">
          <Card className="p-8 border-blue-200 bg-blue-50">
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">No Google Sheet Assigned</h2>
              <p className="text-slate-600 mb-6">
                Enter your Google Sheets URL below to view your analytics dashboard
              </p>
              
              <div className="space-y-4">
                <div>
                  <Input
                    placeholder="https://docs.google.com/spreadsheets/d/YOUR_SHEET_ID/edit?usp=sharing"
                    value={newSheetsUrl}
                    onChange={(e) => setNewSheetsUrl(e.target.value)}
                    className="text-center"
                  />
                  <p className="text-xs text-slate-500 mt-2">
                    Paste your shareable Google Sheets link here
                  </p>
                </div>
                
                <Button 
                  onClick={async () => {
                    if (!newSheetsUrl.trim()) {
                      toast({
                        title: "Error",
                        description: "Please enter a Google Sheets URL",
                        variant: "destructive",
                      });
                      return;
                    }
                    
                    if (!newSheetsUrl.includes('docs.google.com/spreadsheets')) {
                      toast({
                        title: "Error",
                        description: "Please enter a valid Google Sheets URL",
                        variant: "destructive",
                      });
                      return;
                    }
                    
                    setCurrentSheetsUrl(newSheetsUrl);
                    await fetchAnalytics(false, newSheetsUrl);
                  }}
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Load Dashboard
                    </>
                  )}
                </Button>
              </div>
              
              <div className="mt-6 pt-6 border-t border-slate-300">
                <p className="text-sm text-slate-600">
                  <strong>How to get your shareable link:</strong>
                </p>
                <ol className="text-sm text-slate-600 mt-2 space-y-1 text-left max-w-md mx-auto">
                  <li>1. Open your Google Sheet</li>
                  <li>2. Click "Share" in the top-right corner</li>
                  <li>3. Click "Copy link" and paste it above</li>
                </ol>
                {currentUser?.role !== "admin" && (
                  <p className="text-xs text-slate-500 mt-4">
                    To have a sheet permanently assigned, contact your administrator
                  </p>
                )}
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Main Content */}
      {(currentUser?.sheets_url || currentSheetsUrl) && (
        <div className="max-w-7xl mx-auto px-6 py-8">
        {loading && !metrics ? (
          <div className="flex items-center justify-center py-20" data-testid="loading-spinner">
            <div className="text-center">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-[#004aad]" />
              <p className="text-slate-600">Loading analytics data...</p>
            </div>
          </div>
        ) : metrics ? (
          <div className="space-y-8">
            {/* Key Metrics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6">
              {/* Total Leads */}
              <Card 
                className={`p-6 bg-white shadow-lg hover:shadow-xl transition-all border-0 ring-2 cursor-pointer ${
                  !cardFilter ? 'ring-[#004aad]' : 'ring-slate-200'
                }`}
                data-testid="total-leads-card"
                onClick={() => handleCardClick(null)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-600 mb-1">Total Leads</p>
                    <p className="text-3xl font-bold text-slate-900" style={{fontFamily: 'League Spartan, sans-serif'}}>
                      {metrics.call_volume.toLocaleString()}
                    </p>
                  </div>
                  <div className="p-3 bg-[#004aad]/10 rounded-full">
                    <Users className="w-6 h-6 text-[#004aad]" />
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-2">Unique leads in database · Click to view all</p>
              </Card>

              {/* Uncalled Leads */}
              <Card 
                className={`p-6 bg-white shadow-lg hover:shadow-xl transition-all border-0 ring-2 cursor-pointer ${
                  cardFilter?.type === 'uncalled' ? 'ring-orange-500' : 'ring-slate-200'
                }`}
                data-testid="uncalled-leads-card"
                onClick={() => handleCardClick('uncalled')}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-600 mb-1">Uncalled Leads</p>
                    <p className="text-3xl font-bold text-orange-600" style={{fontFamily: 'League Spartan, sans-serif'}}>
                      {uncalledLeadsCount.toLocaleString()}
                    </p>
                  </div>
                  <div className="p-3 bg-orange-100 rounded-full">
                    <Phone className="w-6 h-6 text-orange-600" />
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-2">Leads with 0 call attempts · Click to filter</p>
              </Card>

              {/* Call Attempts */}
              <Card className="p-6 bg-white shadow-lg hover:shadow-xl transition-shadow border-0 ring-1 ring-slate-200" data-testid="call-attempts-card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-600 mb-1">Total Call Attempts</p>
                    <p className="text-3xl font-bold text-slate-900" style={{fontFamily: 'League Spartan, sans-serif'}}>
                      {metrics.call_attempts.toLocaleString()}
                    </p>
                  </div>
                  <div className="p-3 bg-[#004aad]/10 rounded-full">
                    <Phone className="w-6 h-6 text-[#004aad]" />
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-2">Total outbound calls made</p>
              </Card>

              {/* Success Rate */}
              <Card className="p-6 bg-white shadow-lg hover:shadow-xl transition-shadow border-0 ring-1 ring-slate-200" data-testid="success-rate-card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-600 mb-1">Success Rate</p>
                    <p className="text-3xl font-bold text-slate-900" style={{fontFamily: 'League Spartan, sans-serif'}}>
                      {getSuccessRate()}%
                    </p>
                  </div>
                  <div className="p-3 bg-green-100 rounded-full">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-2">Meetings + Callbacks</p>
              </Card>

              {/* Completed Leads */}
              <Card 
                className={`p-6 bg-white shadow-lg hover:shadow-xl transition-all border-0 ring-2 cursor-pointer ${
                  cardFilter?.type === 'completed' ? 'ring-green-500' : 'ring-slate-200'
                }`}
                data-testid="completed-leads-card"
                onClick={() => handleCardClick('completed')}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-600 mb-1">Completed</p>
                    <p className="text-3xl font-bold text-green-600" style={{fontFamily: 'League Spartan, sans-serif'}}>
                      {completedLeadsCount.toLocaleString()}
                    </p>
                  </div>
                  <div className="p-3 bg-green-100 rounded-full">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-2">4+ attempts or closed status · Click to filter</p>
              </Card>

              {/* Remaining Leads */}
              <Card 
                className={`p-6 bg-white shadow-lg hover:shadow-xl transition-all border-0 ring-2 cursor-pointer ${
                  cardFilter?.type === 'remaining' ? 'ring-blue-500' : 'ring-slate-200'
                }`}
                data-testid="remaining-leads-card"
                onClick={() => handleCardClick('remaining')}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-600 mb-1">Remaining</p>
                    <p className="text-3xl font-bold text-blue-600" style={{fontFamily: 'League Spartan, sans-serif'}}>
                      {remainingLeadsCount.toLocaleString()}
                    </p>
                  </div>
                  <div className="p-3 bg-blue-100 rounded-full">
                    <TrendingUp className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-2">Leads to be worked · Click to filter</p>
              </Card>
            </div>

            {/* ROI Section */}
            <Card className="p-6 bg-gradient-to-r from-green-50 to-emerald-50 shadow-lg border-0 ring-1 ring-green-200" data-testid="roi-section-card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-900" style={{fontFamily: 'League Spartan, sans-serif'}}>
                  ROI & Revenue Potential
                </h3>
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
              <Separator className="mb-4" />
              
              {/* Deal Size Input */}
              <div className="mb-6">
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <label className="text-sm font-medium text-slate-700 mb-2 block">
                      Average Deal Size
                    </label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                      <Input
                        type="number"
                        value={dealSize}
                        onChange={(e) => setDealSize(parseInt(e.target.value) || 0)}
                        className="pl-10"
                        placeholder="5000"
                        data-testid="deal-size-input"
                      />
                    </div>
                  </div>
                  <div className="text-sm text-slate-600 mt-6">
                    <p>Adjust deal size to calculate ROI</p>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <p className="text-sm font-medium text-slate-600 mb-1">Interested Leads</p>
                  <p className="text-3xl font-bold text-blue-700" style={{fontFamily: 'League Spartan, sans-serif'}}>
                    {roiMetrics.interestedLeads}
                  </p>
                  <p className="text-xs text-slate-500">Callback, Meeting, Follow up</p>
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium text-slate-600 mb-1">Estimated Commission</p>
                  <p className="text-3xl font-bold text-green-700" style={{fontFamily: 'League Spartan, sans-serif'}}>
                    ${roiMetrics.totalCommission.toLocaleString()}
                  </p>
                  <p className="text-xs text-slate-500">15% commission on interested leads</p>
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium text-slate-600 mb-1">Deal Size Used</p>
                  <p className="text-3xl font-bold text-green-700" style={{fontFamily: 'League Spartan, sans-serif'}}>
                    ${roiMetrics.avgLoanAmount.toLocaleString()}
                  </p>
                  <p className="text-xs text-slate-500">Average policy amount</p>
                </div>
              </div>
            </Card>

            {/* Data Visualization Charts */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              {/* Status Distribution Chart */}
              <Card className="p-6 bg-white shadow-lg border-0 ring-1 ring-slate-200" data-testid="status-chart-card">
                <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{fontFamily: 'League Spartan, sans-serif'}}>
                  Call Status Distribution
                </h3>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={statusData}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        dataKey="value"
                      >
                        {statusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={statusColors[index]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value, name) => [`${value} leads (${statusData.find(d => d.name === name)?.percentage}%)`, name]} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </Card>

              {/* Lead Score Distribution Chart */}
              <Card className="p-6 bg-white shadow-lg border-0 ring-1 ring-slate-200" data-testid="score-chart-card">
                <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{fontFamily: 'League Spartan, sans-serif'}}>
                  Lead Score Distribution
                </h3>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={scoreData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                        {scoreData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={scoreColors[index]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            </div>

            {/* Call Status Breakdown */}
            <Card className="p-6 bg-white shadow-lg border-0 ring-1 ring-slate-200" data-testid="status-breakdown-card">
              <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{fontFamily: 'League Spartan, sans-serif'}}>
                Call Status Breakdown
              </h3>
              <Separator className="mb-4" />
              
              {Object.keys(metrics.status_counts).length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {Object.entries(metrics.status_counts)
                    .sort(([,a], [,b]) => b - a) // Sort by count descending
                    .map(([status, count]) => (
                    <div 
                      key={status} 
                      className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all ${
                        cardFilter?.type === 'status' && cardFilter?.value === status 
                          ? 'bg-blue-100 ring-2 ring-blue-500' 
                          : 'bg-slate-50 hover:bg-slate-100'
                      }`}
                      data-testid={`status-${status.toLowerCase().replace(/\s+/g, '-')}`}
                      onClick={() => handleCardClick('status', status)}
                    >
                      <div>
                        <Badge className={`${getStatusColor(status)} text-xs font-medium`}>
                          {status}
                        </Badge>
                        <p className="text-xs text-slate-500 mt-1">
                          {((count / metrics.call_volume) * 100).toFixed(1)}% of total
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-slate-900" style={{fontFamily: 'League Spartan, sans-serif'}}>
                          {count}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">No status data available</p>
              )}
            </Card>

            {/* Leads Details Table */}
            <Card className="bg-white shadow-lg border-0 ring-1 ring-slate-200" data-testid="leads-table-card">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900" style={{fontFamily: 'League Spartan, sans-serif'}}>
                      {cardFilter ? (
                        cardFilter.type === 'uncalled' ? `Uncalled Leads (${filteredLeads.length})` :
                        cardFilter.type === 'completed' ? `Completed Leads (${filteredLeads.length})` :
                        cardFilter.type === 'remaining' ? `Remaining Leads (${filteredLeads.length})` :
                        cardFilter.type === 'status' ? `${cardFilter.value} Leads (${filteredLeads.length})` :
                        `All Leads Details (${filteredLeads.length} leads)`
                      ) : (
                        `All Leads Details (${filteredLeads.length} leads)`
                      )}
                    </h3>
                    {cardFilter && (
                      <button
                        onClick={() => {
                          setCardFilter(null);
                          setCurrentPage(1);
                        }}
                        className="text-xs text-blue-600 hover:text-blue-800 mt-1 flex items-center gap-1"
                      >
                        <X className="w-3 h-3" />
                        Clear filter
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-4">
                    {/* Call Attempts Filter */}
                    <div className="flex items-center gap-2">
                      <label className="text-sm font-medium text-slate-700">Filter by Calls:</label>
                      <select
                        value={callAttemptsFilter}
                        onChange={(e) => {
                          setCallAttemptsFilter(e.target.value);
                          setCurrentPage(1);
                        }}
                        className="px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="all">All Leads</option>
                        <option value="0">0 Calls (Uncalled)</option>
                        <option value="1">1 Call</option>
                        <option value="2">2 Calls</option>
                        <option value="3+">3+ Calls</option>
                      </select>
                    </div>
                    
                    {/* Search Input */}
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                      <Input
                        placeholder="Search leads..."
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          setCurrentPage(1); // Reset to first page when searching
                        }}
                        className="pl-10 w-64"
                        data-testid="search-input"
                      />
                    </div>
                  </div>
                </div>
                <Separator className="mb-4" />
                
                {filteredLeads.length > 0 ? (
                  <>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="font-semibold">Name</TableHead>
                            {hasBusinessNames && <TableHead className="font-semibold">Business Name</TableHead>}
                            <TableHead className="font-semibold">Phone</TableHead>
                            <TableHead className="font-semibold">Email</TableHead>
                            <TableHead className="font-semibold">Status</TableHead>
                            <TableHead className="font-semibold">Score</TableHead>
                            <TableHead className="font-semibold">Attempts</TableHead>
                            <TableHead className="font-semibold">Direction</TableHead>
                            <TableHead className="font-semibold">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {currentLeads.map((lead, index) => (
                            <TableRow key={index} className="hover:bg-slate-50 cursor-pointer" data-testid={`lead-row-${index}`}>
                              <TableCell className="font-medium">{lead.first_name || 'N/A'}</TableCell>
                              {hasBusinessNames && <TableCell className="text-sm text-slate-600">{lead.business_name || 'N/A'}</TableCell>}
                              <TableCell className="text-sm text-slate-600">{lead.phone_number || 'N/A'}</TableCell>
                              <TableCell className="text-sm text-slate-600 truncate max-w-[200px]" title={lead.email}>
                                {lead.email || 'N/A'}
                              </TableCell>
                              <TableCell>
                                <Badge className={`${getStatusColor(lead.lead_status)} text-xs`}>
                                  {lead.lead_status || 'Unknown'}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-center">
                                <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold ${
                                  lead.lead_score >= 7 ? 'bg-green-100 text-green-800' :
                                  lead.lead_score >= 4 ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-red-100 text-red-800'
                                }`}>
                                  {lead.lead_score}
                                </span>
                              </TableCell>
                              <TableCell className="text-center">
                                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-800 text-xs font-bold">
                                  {lead.call_attempts}
                                </span>
                              </TableCell>
                              <TableCell className="text-center">
                                <Badge className={lead.call_direction === "inbound" ? "bg-green-100 text-green-800" : "bg-blue-100 text-blue-800"}>
                                  {lead.call_direction === "inbound" ? "↓ Inbound" : "↑ Outbound"}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleLeadClick(lead)}
                                  className="text-xs"
                                  data-testid={`view-details-${index}`}
                                >
                                  <Eye className="w-3 h-3 mr-1" />
                                  View Details
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    
                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="flex items-center justify-between mt-6">
                        <div className="text-sm text-slate-600">
                          Showing {((currentPage - 1) * leadsPerPage) + 1} to {Math.min(currentPage * leadsPerPage, filteredLeads.length)} of {filteredLeads.length} leads
                        </div>
                        <div className="flex items-center gap-2">
                          {/* First page and previous group navigation */}
                          {visiblePages[0] > 1 && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCurrentPage(1)}
                                data-testid="first-page-button"
                              >
                                1
                              </Button>
                              {visiblePages[0] > 2 && (
                                <span className="px-2 text-slate-400">...</span>
                              )}
                            </>
                          )}
                          
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                            data-testid="prev-page-button"
                          >
                            <ChevronLeft className="w-4 h-4" />
                            Previous
                          </Button>
                          
                          <div className="flex items-center gap-1">
                            {visiblePages.map(page => (
                              <Button
                                key={page}
                                variant={currentPage === page ? "default" : "outline"}
                                size="sm"
                                onClick={() => setCurrentPage(page)}
                                className={currentPage === page ? "bg-[#004aad] hover:bg-[#003a8c]" : ""}
                                data-testid={`page-${page}-button`}
                              >
                                {page}
                              </Button>
                            ))}
                          </div>
                          
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                            disabled={currentPage === totalPages}
                            data-testid="next-page-button"
                          >
                            Next
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                          
                          {/* Last page and next group navigation */}
                          {visiblePages[visiblePages.length - 1] < totalPages && (
                            <>
                              {visiblePages[visiblePages.length - 1] < totalPages - 1 && (
                                <span className="px-2 text-slate-400">...</span>
                              )}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCurrentPage(totalPages)}
                                data-testid="last-page-button"
                              >
                                {totalPages}
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-slate-500">
                      {searchTerm ? `No leads found matching "${searchTerm}"` : "No leads data available"}
                    </p>
                    {searchTerm && (
                      <Button 
                        variant="outline" 
                        onClick={() => setSearchTerm("")}
                        className="mt-2"
                      >
                        Clear Search
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </Card>

            {/* Auto-refresh Notice */}
            <div className="text-center text-sm text-slate-500">
              <p>Dashboard automatically refreshes every 5 minutes</p>
            </div>
          </div>
        ) : (
          <div className="text-center py-20" data-testid="no-data-message">
            <div className="mb-6">
              <div className="text-4xl mb-4">📊</div>
              <p className="text-slate-900 font-semibold text-lg mb-2">No analytics data available</p>
              <p className="text-slate-600 text-sm mb-1">
                {currentUser?.sheets_url ? 
                  "Click the button below to load your dashboard data" : 
                  "Please contact your administrator to assign a Google Sheets URL"}
              </p>
              {currentUser?.sheets_url && (
                <p className="text-slate-500 text-xs mt-2">
                  Sheet: {currentUser.sheets_url.substring(0, 50)}...
                </p>
              )}
            </div>
            {currentUser?.sheets_url && (
              <Button 
                onClick={() => {
                  console.log('[Load Data Button] Manually triggering data fetch');
                  fetchAnalytics(false, currentUser.sheets_url);
                }} 
                disabled={loading}
                className="bg-[#004aad] hover:bg-[#003a8c] text-white"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Loading...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Load Dashboard Data
                  </>
                )}
              </Button>
            )}
          </div>
        )}
        
        {/* Lead Details Modal */}
        <LeadDetailsModal 
          lead={selectedLead} 
          isOpen={showLeadModal} 
          onClose={() => setShowLeadModal(false)} 
        />
      </div>
      )}
      
      <Toaster />
    </div>
  );
}

export default App;
